/**
 * LetterGenerationProgress.jsx
 * 
 * Real-time progress UI for letter generation, powered by backend SSE updates.
 * Shows ONLY actual backend stage progression - no fake timers, ETAs, or filler steps.
 * 
 * REAL STAGES (strict mapping to backend):
 * 1. Locking plan
 * 2. Building queue
 * 3. Generating letter X of Y
 * 4. Validating letter X of Y
 * 5. Saving letter X of Y
 * 6. Finalizing
 * 
 * Progress is based SOLELY on: (completed letters / total letters)
 * No artificial delays, no fake percentages, no ETA guessing.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, AlertCircle, Loader2, FileText, AlertTriangle, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../../utils/authUtils';
import {
  generationHasLetters,
  isFatalSseError,
  isFinalPackageReady,
  isNonFatalPlanReduced,
  logGenerationResultAudit,
  logStep5ResultCounts,
  normalizeGenerationResult,
  packageReconciliationFailureMessage,
  pollUntilLettersAvailable,
  shouldProcessInlineTerminalResult,
} from '../../utils/generationResultUtils';
import {
  extractApiValidationMessage,
  logSanitizedValidationError,
} from '../../utils/extractApiValidationMessage';

/**
 * REAL BACKEND STAGES - These are the only stages that matter.
 * Progress comes from the backend SSE stream, not from frontend simulation.
 * 
 * The frontend only displays what the backend reports.
 * No fake steps, no ETA calculations, no weight-based progress.
 */
const REAL_STAGES = [
  {
    key: 'locking_plan',
    label: 'Locking generation plan',
    help: 'Securing your dispute configuration.',
  },
  {
    key: 'building_queue',
    label: 'Building letter queue',
    help: 'Preparing the sequence of letters to generate.',
  },
  {
    key: 'generating',
    label: 'Generating letters',
    help: 'AI is composing your dispute letters.',
  },
  {
    key: 'validating',
    label: 'Validating letters',
    help: 'Checking letter quality and compliance.',
  },
  {
    key: 'saving',
    label: 'Saving letters',
    help: 'Persisting letters to database.',
  },
  {
    key: 'complete',
    label: 'Complete',
    help: 'All letters generated successfully.',
  },
];

// Map backend stage keys to our simplified display stages
const BACKEND_TO_DISPLAY_STAGE = {
  'initializing': 'locking_plan',
  'locking_plan': 'locking_plan',
  'loading_context': 'locking_plan',
  'scoping_items': 'building_queue',
  'building_queue': 'building_queue',
  'resolving_prompt': 'building_queue',
  'preparing_evidence': 'building_queue',
  'preparing_identity': 'building_queue',
  'validating_identity': 'building_queue',
  'generating_ai': 'generating',
  'generating_draft': 'generating',
  'reviewing_draft': 'validating',
  'validating_draft': 'validating',
  'quality_check': 'validating',
  'saving_letter': 'saving',
  'finalizing': 'saving',
  'complete': 'complete',
  'error': 'complete',
};

// Check if we should use simulated progress (dev only - should be FALSE in production)
const USE_SIMULATED_PROGRESS = process.env.REACT_APP_USE_SIMULATED_PROGRESS === 'true';

// Feature flag for durable generation (production default)
const USE_DURABLE_GENERATION = process.env.REACT_APP_USE_DURABLE_GENERATION !== 'false';

const LetterGenerationProgress = ({
  selectedAccountCount = 0,
  selectedInquiryCount = 0,
  selectedPiCount = 0,
  // NEW: Use Step 3's resolved totals as source of truth
  totalItems = 0,
  totalPackages = 0,
  totalLetters = 0,
  roundId = '',
  isGenerating = false,
  onComplete,
  onGenerationResult,
  // NEW: Hide wizard stepper (parent already renders one)
  hideWizardStepper = false,
  // Generation request data - needed for real backend generation
  generationRequest = null,
  getAuthHeaders = () => ({}),
  onBackToReview = null,
  onRetryGeneration = null,
}) => {
  // Progress state - ALL values come from backend SSE, not frontend calculation
  const [sessionId, setSessionId] = useState(null);
  const [jobId, setJobId] = useState(null); // For durable job tracking
  const [backendStage, setBackendStage] = useState('initializing'); // Raw backend stage
  const [displayStage, setDisplayStage] = useState('locking_plan'); // Mapped display stage
  const [progressPercent, setProgressPercent] = useState(0); // From backend ONLY
  const [completedStages, setCompletedStages] = useState([]);
  const [message, setMessage] = useState('Starting letter generation...');
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [preflightFailure, setPreflightFailure] = useState(null);
  const [useSimulation, setUseSimulation] = useState(
    USE_SIMULATED_PROGRESS && process.env.NODE_ENV !== 'production'
  );
  
  // NEW: Recovery mode state - for graceful disconnect handling
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryStatus, setRecoveryStatus] = useState(null); // { completed, failed, pending, total }
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Letter-by-letter progress tracking - THE REAL PROGRESS SOURCE
  const [letterProgress, setLetterProgress] = useState(null);
  
  // Refs
  const eventSourceRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasStartedRef = useRef(false);
  const checkAndRecoverProgressRef = useRef(null);
  const fetchGenerationResultRef = useRef(null);
  const finalPackageTimeoutRef = useRef(null);
  const statusPollIntervalRef = useRef(null);
  const lastProgressTickRef = useRef(0);
  const lastProgressValueRef = useRef(0);
  const expectedTotalLettersRef = useRef(totalLetters || 0);
  const submittedPackageIdsRef = useRef([]);

  useEffect(() => {
    const submittedIds =
      generationRequest?.expected_totals?.submitted_package_ids
      || generationRequest?.expected_totals?.expected_package_ids
      || [];
    const pinnedCount =
      (Array.isArray(submittedIds) && submittedIds.length)
      || generationRequest?.expected_totals?.expected_package_count
      || generationRequest?.expected_totals?.total_letters
      || totalLetters
      || 0;
    expectedTotalLettersRef.current = pinnedCount;
    submittedPackageIdsRef.current = Array.isArray(submittedIds) ? submittedIds : [];
  }, [totalLetters, generationRequest]);
  const sessionIdRef = useRef(null);
  const isCompleteRef = useRef(false);
  const inlineResultHandledRef = useRef(false);
  const durableResultBlockedRef = useRef(false);
  
  const API = getApiUrl();

  useEffect(() => {
    isCompleteRef.current = isComplete;
  }, [isComplete]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Map backend stage to display stage
  const getDisplayStage = useCallback((backendKey) => {
    return BACKEND_TO_DISPLAY_STAGE[backendKey] || 'locking_plan';
  }, []);

  // Get current stage data for display
  const currentStageData = REAL_STAGES.find(s => s.key === displayStage) || REAL_STAGES[0];

  // Start real backend generation
  const startRealGeneration = useCallback(async () => {
    if (!generationRequest) {
      console.warn('[LetterGenerationProgress] No generationRequest provided');
      setPreflightFailure({
        title: 'We could not start letter generation',
        body: 'Your selected items and dispute plan are saved. Review the issue and try again.',
        detail: 'Missing generation request payload.',
      });
      setUseSimulation(false);
      setError('Missing generation request payload.');
      return;
    }

    const forceRegenerate = generationRequest.force_regenerate !== false;
    if (forceRegenerate) {
      console.log('[REGENERATE FLOW] force_regenerate=true');
      console.log('[REGENERATE FLOW] calling durable-generate');
      try {
        Object.keys(sessionStorage)
          .filter((key) => key.startsWith('generation_'))
          .forEach((key) => sessionStorage.removeItem(key));
      } catch (_) {
        // ignore storage errors
      }
    }
    
    try {
      console.log('[GEN PROGRESS] started');
      setPreflightFailure(null);
      setError(null);
      setUseSimulation(false);
      setCompletedStages([]);
      setProgressPercent(0);
      setDisplayStage('locking_plan');
      const endpoint = USE_DURABLE_GENERATION 
        ? `${API}/letter-preview/durable-generate`
        : `${API}/letter-preview/start-generation`;
      
      console.log(`[LetterGenerationProgress] Starting generation with ${USE_DURABLE_GENERATION ? 'DURABLE' : 'standard'} endpoint`);
      console.log('[GEN PROGRESS] request sent', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(generationRequest),
      });
      
      if (!response.ok) {
        const detailMessage = await extractApiValidationMessage(response);
        logSanitizedValidationError('[DURABLE GENERATE VALIDATION]', response.status, detailMessage);
        setUseSimulation(false);
        setCompletedStages([]);
        setProgressPercent(0);
        setLetterProgress(null);
        setSessionId(null);
        setJobId(null);
        setIsComplete(false);
        setRecoveryMode(false);
        setPreflightFailure({
          title: 'We could not start letter generation',
          body: 'Your selected items and dispute plan are saved. Review the issue and try again.',
          detail: detailMessage,
        });
        setError(detailMessage);
        console.log('[GEN PROGRESS] error state', detailMessage);
        return;
      }
      
      const data = await response.json();
      setSessionId(data.session_id);
      sessionIdRef.current = data.session_id;
      if (data.run_id) {
        setJobId(data.run_id);
      }
      if (data.reused) {
        console.log('[REGENERATE FLOW] reused existing run_id=%s', data.run_id);
      } else if (data.run_id) {
        console.log('[REGENERATE FLOW] new_run_id=%s', data.run_id);
      }
      if (data.generation_debug) {
        console.log('[GEN DEBUG]', {
          run_id: data.run_id,
          commit: data.generation_debug.commit,
          final_plan_methods: data.generation_debug.final_plan_methods,
          queued_methods: data.generation_debug.queued_methods,
          generated_methods: data.generation_debug.generated_methods,
          total_letters: data.generation_debug.total_letters,
          letters_by_method: data.generation_debug.letters_by_method,
          blocked_methods: data.generation_debug.blocked_methods,
        });
      }
      console.log('[GEN PROGRESS] generation_run_id', data.run_id || data.session_id);
      lastProgressTickRef.current = Date.now();
      lastProgressValueRef.current = 0;
      
      // Store run_id if using durable generation (for resume capability)
      if (data.run_id) {
        sessionStorage.setItem(`generation_run_${data.session_id}`, data.run_id);
        console.log('[LetterGenerationProgress] Stored run_id for resume:', data.run_id);
      }
      
      // Connect to SSE for progress updates (use durable or standard endpoint)
      connectToSSE(data.session_id, USE_DURABLE_GENERATION);
      
    } catch (err) {
      console.error('[LetterGenerationProgress] Failed to start real generation:', err);
      console.log('[GEN PROGRESS] error state', err.message);
      setUseSimulation(false);
      setPreflightFailure({
        title: 'We could not start letter generation',
        body: 'Your selected items and dispute plan are saved. Review the issue and try again.',
        detail: err.message,
      });
      setError(`Failed to start generation: ${err.message}`);
    }
  }, [generationRequest, API]);

  // Connect to SSE for progress updates
  const connectToSSE = useCallback((sid, useDurable = false) => {
    const sseUrl = useDurable
      ? `${API}/letter-preview/durable-progress/${sid}`
      : `${API}/letter-preview/generation-progress/${sid}`;
    
    console.log(`[LetterGenerationProgress] Connecting to SSE: ${sseUrl}`);
    
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[GEN PROGRESS] poll tick', {
          stage: data.stage,
          progress: data.progress,
          current_letter: data.current_letter,
          total_letters: data.total_letters,
          is_complete: data.is_complete,
          plan_reduced: data.plan_reduced,
          status: data.status,
        });
        console.log('[GEN PROGRESS] poll response status', data.stage || 'update');

        if (data.run_id) {
          sessionStorage.setItem(`generation_run_${sid}`, data.run_id);
        }

        // Fail fast when lock dropped packages that were NOT superseded by combo.
        // Non-fatal supersession: top-level or nested continue_run/audit_only, or
        // superseded_by_combo reason — keep going and do not show missing-package error.
        const planReducedRaw = data.plan_reduced;
        const planReducedObj =
          planReducedRaw && typeof planReducedRaw === 'object' ? planReducedRaw : null;
        const planReducedReason = data.reason || planReducedObj?.reason || '';

        if (isNonFatalPlanReduced(data)) {
          const lockedIds =
            data.locked_package_ids ||
            (Array.isArray(data.locked_plan)
              ? data.locked_plan.map((p) => (typeof p === 'string' ? p : p?.package_id)).filter(Boolean)
              : []);
          if (Array.isArray(lockedIds) && lockedIds.length > 0) {
            expectedTotalLettersRef.current = lockedIds.length;
            submittedPackageIdsRef.current = lockedIds;
            console.info('[GEN PROGRESS] plan_reduced audit (superseded_by_combo)', {
              droppedIds: data.dropped_package_ids,
              superseded: data.superseded || data.superseded_packages,
              rewrittenDenominator: lockedIds.length,
              reason: planReducedReason,
            });
          }
        } else if (planReducedRaw) {
          const droppedIds = data.dropped_package_ids || data.missing_packages || [];
          const droppedMethods = data.dropped_methods || [];
          const reason = planReducedReason || 'submitted_vs_locked_package_id_mismatch';
          console.error('[GEN PROGRESS] plan_reduced', {
            droppedIds,
            droppedMethods,
            reason,
            superseded: data.superseded,
          });
          isCompleteRef.current = true;
          setProgressPercent(Number.isFinite(Number(data.progress)) ? Number(data.progress) : 100);
          setCompletedStages(REAL_STAGES.map(s => s.key));
          eventSource.close();
          if (statusPollIntervalRef.current) {
            clearInterval(statusPollIntervalRef.current);
            statusPollIntervalRef.current = null;
          }
          setIsComplete(true);
          setError(
            `Generation incomplete. Missing packages: ${
              droppedIds.length ? droppedIds.join(', ') : (droppedMethods.join(', ') || reason)
            }`
          );
          // Deliver terminal payload before treating as hard failure.
          if (shouldProcessInlineTerminalResult(data) && data.result && onGenerationResult) {
            onGenerationResult(normalizeGenerationResult(data.result, { logDrops: true }));
          }
          return;
        }
        
        if (data.is_complete || data.status === 'incomplete' || data.stage === 'incomplete' || data.stream_terminal) {
          console.log('[GEN PROGRESS] setting complete');
          isCompleteRef.current = true;
          const terminalProgress = Number.isFinite(Number(data.progress))
            ? Number(data.progress)
            : (data.is_complete || data.packages_complete ? 100 : undefined);
          if (terminalProgress != null) {
            setProgressPercent(terminalProgress);
          }
          setLetterProgress(null);
          if (data.is_complete || data.packages_complete) {
            setCompletedStages(REAL_STAGES.map(s => s.key));
          }
          eventSource.close();
          if (statusPollIntervalRef.current) {
            clearInterval(statusPollIntervalRef.current);
            statusPollIntervalRef.current = null;
          }

          // Process inline terminal result BEFORE treating data.error as hard failure.
          // Incomplete terminals may carry zero letters + missing_packages.
          if (shouldProcessInlineTerminalResult(data)) {
            if (data.result) {
              const normalized = normalizeGenerationResult(data.result, { logDrops: true });
              if (normalized?.generation_debug) {
                console.log('[GEN DEBUG]', {
                  run_id: normalized.run_id,
                  commit: normalized.generation_debug.commit,
                  final_plan_methods: normalized.generation_debug.final_plan_methods,
                  queued_methods: normalized.generation_debug.queued_methods,
                  generated_methods: normalized.generation_debug.generated_methods,
                  total_letters: normalized.generation_debug.total_letters ?? normalized.total_letters,
                  letters_by_method: normalized.generation_debug.letters_by_method,
                  blocked_methods: normalized.generation_debug.blocked_methods,
                });
              }
              logGenerationResultAudit(normalized, 'SSE INLINE RESULT');
              logStep5ResultCounts(normalized, 'sse_inline', { run_id: normalized.run_id });
              if (!isFinalPackageReady(normalized)) {
                // Still deliver incomplete terminal context to the parent, then
                // optionally refresh from durable-result for hydration.
                inlineResultHandledRef.current = true;
                setIsComplete(true);
                if (onGenerationResult) {
                  onGenerationResult(normalized);
                }
                if (isFatalSseError(data)) {
                  setError(data.error);
                } else if (
                  !normalized.is_complete &&
                  (normalized.missing_package_ids || data.missing_packages)?.length
                ) {
                  const missing =
                    normalized.missing_package_ids || data.missing_packages || [];
                  setError(
                    data.failure_message ||
                      `Generation incomplete. Missing packages: ${missing.slice(0, 6).join(', ')}`
                  );
                }
                if (fetchGenerationResultRef.current) {
                  fetchGenerationResultRef.current(sid, useDurable);
                }
                return;
              }
              inlineResultHandledRef.current = true;
              setIsComplete(true);
              if (onGenerationResult) {
                onGenerationResult(normalized);
              }
              if (onComplete) {
                setTimeout(() => onComplete(normalized), 1500);
              }
            } else if (fetchGenerationResultRef.current) {
              fetchGenerationResultRef.current(sid, useDurable);
            }
          } else if (isFatalSseError(data)) {
            console.log('[GEN PROGRESS] error state', data.error);
            setIsComplete(true);
            setError(data.error);
          } else if (fetchGenerationResultRef.current) {
            fetchGenerationResultRef.current(sid, useDurable);
          }
          return;
        }
        
        // Update stage from backend - map to display stage
        if (data.stage) {
          setBackendStage(data.stage);
          const mapped = BACKEND_TO_DISPLAY_STAGE[data.stage] || 'locking_plan';
          setDisplayStage(mapped);
          
          const stageIndex = REAL_STAGES.findIndex(s => s.key === mapped);
          if (stageIndex >= 0) {
            setCompletedStages(REAL_STAGES.slice(0, stageIndex).map(s => s.key));
          }
        }
        
        if (data.message) setMessage(data.message);
        // In-progress must never treat status messages as hard error.
        // Only fatal error terminals set data.error.
        if (isFatalSseError(data)) {
          console.log('[GEN PROGRESS] error state', data.error);
          setError(data.error);
        }
        
        const expectedTotal = expectedTotalLettersRef.current
          || data.total_letters
          || data.total_packages
          || 1;
        let nextProgress = typeof data.progress === 'number' ? data.progress : lastProgressValueRef.current;

        if (data.current_letter !== undefined && data.total_letters !== undefined) {
          // Pin denominator to submitted package count — never shrink to locked-plan count.
          const pinned = expectedTotalLettersRef.current || 0;
          const total = Math.max(pinned || data.total_letters || expectedTotal || 1, 1);
          const current = data.current_letter || 0;
          const letterProgressPercent = Math.round((current / total) * 100);
          console.log('[GEN PROGRESS] completed packages count', current);
          console.log('[GEN PROGRESS] total expected packages count', total);
          setLetterProgress({
            current_letter: current,
            total_letters: expectedTotalLettersRef.current || total,
            package_label: data.current_package || data.package_label,
            bureau: data.current_bureau || data.bureau,
          });
          nextProgress = Math.max(nextProgress, letterProgressPercent);
        } else if (data.letter_progress) {
          setLetterProgress(data.letter_progress);
        }

        if (typeof nextProgress === 'number' && nextProgress >= 0) {
          console.log('[GEN PROGRESS] setting progress', nextProgress);
          setProgressPercent(Math.min(nextProgress, 99));
          lastProgressValueRef.current = nextProgress;
          lastProgressTickRef.current = Date.now();
        }
      } catch (err) {
        console.error('[LetterGenerationProgress] SSE parse error:', err);
      }
    };
    
    eventSource.onerror = (err) => {
      console.error('[LetterGenerationProgress] SSE error:', err);
      eventSource.close();
      
      // DURABLE: Don't immediately error - enter recovery mode
      if (!isComplete && useDurable) {
        // Enter recovery mode with helpful UI
        setRecoveryMode(true);
        setError(null); // Clear any error - we're recovering
        setMessage('We lost connection to live updates. Your generation may still be running.');
        // Use ref to avoid circular dependency
        if (checkAndRecoverProgressRef.current) {
          checkAndRecoverProgressRef.current(sid);
        }
      } else if (!isComplete) {
        // Legacy: Show error for non-durable generation
        setRecoveryMode(true);
        setMessage('Connection lost. Your generation may still be running.');
      }
    };
    
    return () => {
      eventSource.close();
    };
  }, [API, isComplete, onGenerationResult, onComplete]);

  // DURABLE: Check and recover progress after connection loss
  const checkAndRecoverProgress = useCallback(async (sid, onComplete = null) => {
    setIsRefreshingStatus(true);
    try {
      const runId = sessionStorage.getItem(`generation_run_${sid}`);
      
      // Try new durable job endpoint first (optional — letter-preview durable flow may not register here)
      let response = await fetch(`${API}/durable-jobs/job-status-by-session/${sid}`);
      
      // Fallback to old endpoint if new one doesn't exist
      if (!response.ok && runId) {
        response = await fetch(`${API}/letter-preview/run-status/${runId}`);
      }

      if (!response.ok) {
        // Non-fatal: avoid durable-result polling when status endpoints are unavailable
        return null;
      }
      
      const status = await response.json();
      console.log('[LetterGenerationProgress] Recovered status:', status);
      
      // Update recovery status for UI
      setRecoveryStatus({
        completed: status.completed_count || status.completed_letters || 0,
        failed: status.failed_count || 0,
        pending: status.pending_count || 0,
        total: status.expected_total_letters || status.total_letters || totalLetters,
        workerActive: status.worker_active,
        jobId: status.job_id,
      });
      
      if (status.job_id) {
        setJobId(status.job_id);
      }
      
      if (status.status === 'completed' || status.status === 'completed_with_errors') {
        // Generation finished while we were disconnected
        setRecoveryMode(false);
        setProgressPercent(100);
        setMessage(`Generated ${status.completed_count || status.completed_letters} of ${status.expected_total_letters || status.total_letters} letters`);
        
        if (fetchGenerationResultRef.current) {
          fetchGenerationResultRef.current(sid, true);
        }
      } else if (status.is_resumable || status.worker_active) {
        // Still in progress - show status and allow reconnect
        const completed = status.completed_count || status.completed_letters || 0;
        const total = status.expected_total_letters || status.total_letters || totalLetters || 1; // Prevent division by zero
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
        setProgressPercent(percent);
        setMessage(`Generation in progress: ${completed}/${total} letters complete`);
      } else if (status.status === 'failed') {
        setMessage(`Generation failed. ${status.failed_count || 0} letters failed.`);
      } else {
        setMessage(`Status: ${status.status}. ${status.completed_count || 0}/${status.expected_total_letters || 0} complete.`);
      }
      
      return status;
    } catch (err) {
      console.error('[LetterGenerationProgress] Failed to recover:', err);
      setMessage('Unable to check generation status. Your generation may still be running on the server.');
      return null;
    } finally {
      setIsRefreshingStatus(false);
    }
  }, [API, totalLetters]);

  // Keep ref updated for use in SSE error handler
  useEffect(() => {
    checkAndRecoverProgressRef.current = checkAndRecoverProgress;
  }, [checkAndRecoverProgress]);

  // Refresh status button handler
  const handleRefreshStatus = useCallback(async () => {
    if (sessionId) {
      await checkAndRecoverProgress(sessionId);
    }
  }, [sessionId, checkAndRecoverProgress]);

  // Reconnect to live updates
  const handleReconnect = useCallback(async () => {
    if (!sessionId) return;
    
    setIsReconnecting(true);
    setMessage('Reconnecting to live updates...');
    
    try {
      // First refresh status
      await checkAndRecoverProgress(sessionId);
      
      // Then reconnect to SSE
      connectToSSE(sessionId, USE_DURABLE_GENERATION);
      setRecoveryMode(false);
    } catch (err) {
      console.error('[LetterGenerationProgress] Reconnect failed:', err);
      setMessage('Failed to reconnect. Please try again.');
    } finally {
      setIsReconnecting(false);
    }
  }, [sessionId, checkAndRecoverProgress, connectToSSE]);

  // Retry failed letters
  const handleRetryFailed = useCallback(async () => {
    if (!jobId && !sessionId) return;
    
    setIsRetrying(true);
    setMessage('Retrying failed letters...');
    
    try {
      const id = jobId || sessionId;
      const response = await fetch(`${API}/durable-jobs/retry-failed/${id}`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to retry');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setMessage(`Retrying ${result.retried_count} failed letters...`);
        setRecoveryMode(false);
        // Reconnect to live updates
        connectToSSE(sessionId, true);
      } else {
        setMessage(result.message || 'No letters to retry');
      }
    } catch (err) {
      console.error('[LetterGenerationProgress] Retry failed:', err);
      setMessage('Failed to retry. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  }, [jobId, sessionId, API, connectToSSE]);

  // Fetch the full generation result
  const fetchGenerationResult = useCallback(async (sid, useDurable = false) => {
    if (inlineResultHandledRef.current) {
      return;
    }
    try {
      const endpoint = useDurable
        ? `${API}/letter-preview/durable-result/${sid}`
        : `${API}/letter-preview/generation-result/${sid}`;
      
      let result = null;
      const response = await fetch(endpoint);

      if (response.status === 400) {
        durableResultBlockedRef.current = true;
        console.log('[GEN PROGRESS] durable-result not ready (400), skipping repeat fetch');
        return;
      }
      
      if (response.ok) {
        result = await response.json();
      }

      if (result?.generation_debug) {
        console.log('[GEN DEBUG]', {
          run_id: result.run_id,
          commit: result.generation_debug.commit,
          final_plan_methods: result.generation_debug.final_plan_methods,
          queued_methods: result.generation_debug.queued_methods,
          generated_methods: result.generation_debug.generated_methods,
          total_letters: result.generation_debug.total_letters ?? result.total_letters,
          letters_by_method: result.generation_debug.letters_by_method,
          blocked_methods: result.generation_debug.blocked_methods,
        });
      }

      const runId =
        result?.run_id ||
        sessionStorage.getItem(`generation_run_${sid}`) ||
        null;

      let normalized = result ? normalizeGenerationResult(result, { logDrops: true }) : null;
      logGenerationResultAudit(normalized, 'FETCH GENERATION RESULT');
      if (normalized) {
        logStep5ResultCounts(normalized, 'durable_result', { run_id: runId });
      }

      if (!normalized || !generationHasLetters(normalized)) {
        normalized = await pollUntilLettersAvailable({
          apiUrl: API.replace(/\/api$/, ''),
          sessionId: sid,
          useDurable,
          roundId,
          runId,
          forceRegenerate: generationRequest?.force_regenerate !== false,
          getAuthHeaders,
        });
        if (normalized) {
          normalized = normalizeGenerationResult(normalized, { logDrops: true });
          logGenerationResultAudit(normalized, 'FETCH GENERATION RESULT (polled)');
          logStep5ResultCounts(normalized, normalized._result_source || 'durable_result_polled', {
            run_id: runId,
          });
        }
      }

      if (!normalized || !isFinalPackageReady(normalized)) {
        logGenerationResultAudit(normalized, 'FETCH GENERATION RESULT (failed)');
        setError(packageReconciliationFailureMessage(normalized));
        setIsComplete(true);
        return;
      }

      setIsComplete(true);
      inlineResultHandledRef.current = true;
      isCompleteRef.current = true;
      
      // Pass result to parent
      if (onGenerationResult) {
        onGenerationResult(normalized);
      }
      
      // Trigger completion callback after brief delay to show success state
      setTimeout(() => {
        if (onComplete) {
          onComplete(normalized);
        }
      }, 1200);
      
    } catch (err) {
      console.error('[LetterGenerationProgress] Failed to fetch result:', err);
      logGenerationResultAudit(null, 'FETCH GENERATION RESULT (exception)');
      setError('Letters were not generated yet. Please retry generation.');
      setIsComplete(true);
    }
  }, [API, onComplete, onGenerationResult, roundId, getAuthHeaders, generationRequest]);

  // Keep ref updated for use in SSE handlers
  useEffect(() => {
    fetchGenerationResultRef.current = fetchGenerationResult;
  }, [fetchGenerationResult]);

  // If progress reaches 100% but final package never arrives, recover or show error
  useEffect(() => {
    if (progressPercent < 100 || isComplete || error || !sessionId) {
      if (finalPackageTimeoutRef.current) {
        clearTimeout(finalPackageTimeoutRef.current);
        finalPackageTimeoutRef.current = null;
      }
      return undefined;
    }

    finalPackageTimeoutRef.current = setTimeout(() => {
      if (fetchGenerationResultRef.current) {
        fetchGenerationResultRef.current(sessionId, true);
      } else {
        setError('Generation finished but final package is not ready. Please retry.');
        setIsComplete(true);
      }
    }, 8000);

    return () => {
      if (finalPackageTimeoutRef.current) {
        clearTimeout(finalPackageTimeoutRef.current);
        finalPackageTimeoutRef.current = null;
      }
    };
  }, [progressPercent, isComplete, error, sessionId]);

  // Simulated progress (explicit opt-in only — never for generation failures)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!USE_SIMULATED_PROGRESS) return;
    if (!isGenerating || !useSimulation || isComplete || preflightFailure) return;
    
    // Simple simulation for dev - just show basic stages
    const totalDuration = 30; // seconds - allow realistic time
    let elapsed = 0;
    
    const timer = setInterval(() => {
      elapsed += 1;
      
      // Simple linear progress through stages
      if (elapsed < 3) {
        setDisplayStage('locking_plan');
        setMessage('Locking generation plan...');
        setCompletedStages([]);
      } else if (elapsed < 6) {
        setDisplayStage('building_queue');
        setMessage('Building letter queue...');
        setCompletedStages(['locking_plan']);
      } else if (elapsed < 22) {
        setDisplayStage('generating');
        setMessage('Generating letters with AI...');
        setCompletedStages(['locking_plan', 'building_queue']);
        // Show letter progress simulation
        const letterNum = Math.min(3, Math.floor((elapsed - 6) / 5) + 1);
        setLetterProgress({
          current_letter: letterNum,
          total_letters: 3,
          package_label: 'Demo Package',
          bureau: ['Equifax', 'Experian', 'TransUnion'][letterNum - 1],
        });
      } else if (elapsed < 26) {
        setDisplayStage('validating');
        setMessage('Validating letters...');
        setCompletedStages(['locking_plan', 'building_queue', 'generating']);
        setLetterProgress(null);
      } else if (elapsed < totalDuration) {
        setDisplayStage('saving');
        setMessage('Saving letters...');
        setCompletedStages(['locking_plan', 'building_queue', 'generating', 'validating']);
      }
      
      // Update progress percentage based on elapsed time
      const percent = Math.min(95, Math.round((elapsed / totalDuration) * 100));
      setProgressPercent(percent);
      
      // Complete after total duration
      if (elapsed >= totalDuration) {
        setIsComplete(true);
        setProgressPercent(100);
        setDisplayStage('complete');
        setCompletedStages(REAL_STAGES.map(s => s.key));
        setMessage('Generation complete');
        clearInterval(timer);
        
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 1200);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isGenerating, useSimulation, isComplete, onComplete, preflightFailure]);

  // Reset per-round generation guards when round changes
  useEffect(() => {
    hasStartedRef.current = false;
    inlineResultHandledRef.current = false;
    durableResultBlockedRef.current = false;
    isCompleteRef.current = false;
    setIsComplete(false);
    setError(null);
    setPreflightFailure(null);
    setSessionId(null);
    setJobId(null);
    sessionIdRef.current = null;
  }, [roundId]);

  // Start generation only once a payload is available (avoids locking hasStarted with null request)
  useEffect(() => {
    if (!isGenerating || !generationRequest || hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    if (!useSimulation) {
      startRealGeneration();
    }
    // If using simulation, the simulation effect handles it
  }, [isGenerating, useSimulation, generationRequest, startRealGeneration]);

  // Poll backend status when SSE progress stalls (background tab throttling safety net)
  useEffect(() => {
    if (!sessionId || isComplete || error || useSimulation) {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
      return undefined;
    }

    statusPollIntervalRef.current = setInterval(async () => {
      const stalledForMs = Date.now() - (lastProgressTickRef.current || Date.now());
      if (stalledForMs < 8000 || isCompleteRef.current || inlineResultHandledRef.current) {
        return;
      }

      const sid = sessionIdRef.current;
      if (!sid) return;

      console.log('[GEN PROGRESS] timeout fallback triggered', { stalledForMs, sid });
      try {
        const runId = sessionStorage.getItem(`generation_run_${sid}`);
        if (!runId) {
          // No run_id yet — generation still starting; avoid durable-result 400 spam
          return;
        }

        const statusResponse = await fetch(`${API}/letter-preview/run-status/${runId}`);
        if (statusResponse?.ok) {
          const status = await statusResponse.json();
          console.log('[GEN PROGRESS] poll response status', status.status);
          const completed = status.completed_letters || 0;
          const total = status.expected_totals?.total_letters || status.total_letters || expectedTotalLettersRef.current || 1;
          const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
          console.log('[GEN PROGRESS] completed packages count', completed);
          console.log('[GEN PROGRESS] total expected packages count', total);
          if (percent > lastProgressValueRef.current) {
            console.log('[GEN PROGRESS] setting progress', percent);
            setProgressPercent(Math.min(percent, 99));
            lastProgressValueRef.current = percent;
            lastProgressTickRef.current = Date.now();
          }
          if (
            (status.status === 'completed'
              || status.status === 'completed_with_errors'
              || status.status === 'failed')
            && fetchGenerationResultRef.current
            && !inlineResultHandledRef.current
          ) {
            fetchGenerationResultRef.current(sid, true);
          }
        }
      } catch (pollErr) {
        console.log('[GEN PROGRESS] error state', pollErr.message);
      }
    }, 4000);

    return () => {
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
        statusPollIntervalRef.current = null;
      }
    };
  }, [sessionId, isComplete, error, useSimulation, API]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (statusPollIntervalRef.current) {
        clearInterval(statusPollIntervalRef.current);
      }
    };
  }, []);

  // Prefer backend package reconciliation totals over stale preview totalLetters.
  // Backend expected_package_count is the canonical final package count.
  const backendExpected =
    letterProgress?.expected_package_count
    || letterProgress?.expected_letter_count
    || letterProgress?.total_letters
    || 0;
  const actualTotalLetters =
    backendExpected
    || (Array.isArray(letterProgress?.expected_package_ids)
      ? letterProgress.expected_package_ids.length
      : 0)
    || totalLetters
    || 0;
  const statusDisplay = isComplete 
    ? 'Complete' 
    : error 
      ? 'Error' 
      : letterProgress 
        ? `Overall: Letter ${letterProgress.current_letter} of ${actualTotalLetters}`
        : 'Processing...';

  return (
    <div className="generation-progress-shell" data-testid="letter-generation-progress">
      <style>{`
        .generation-progress-shell {
          --gp-bg: #eef7f2;
          --gp-card: #ffffff;
          --gp-line: #dfe8e2;
          --gp-text: #18221d;
          --gp-muted: #66736d;
          --gp-green: #12a36b;
          --gp-green-dark: #0b7c52;
          --gp-green-soft: #e7f7ef;
          --gp-gold: #d4a735;
          --gp-gold-soft: #fff7e7;
          --gp-red: #dc2626;
          --gp-red-soft: #fef2f2;
          --gp-shadow: 0 18px 48px rgba(10,40,28,.08);
          --gp-radius: 24px;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, sans-serif;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: hidden;
          box-sizing: border-box;
        }
        
        /* DARK MODE TOKENS */
        .dark .generation-progress-shell {
          --gp-bg: transparent;
          --gp-card: rgba(10, 38, 28, 0.75);
          --gp-line: rgba(142, 218, 182, 0.15);
          --gp-text: #EAF7F0;
          --gp-muted: #93AE9F;
          --gp-green: #10b981;
          --gp-green-dark: #059669;
          --gp-green-soft: rgba(16, 185, 129, 0.12);
          --gp-gold: #f59e0b;
          --gp-gold-soft: rgba(245, 158, 11, 0.12);
          --gp-red: #ef4444;
          --gp-red-soft: rgba(239, 68, 68, 0.12);
          --gp-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
        }
        
        .dark .generation-progress-shell .gp-dot {
          background: rgba(10, 38, 28, 0.75);
          border-color: rgba(142, 218, 182, 0.15);
          color: #93AE9F;
        }
        
        .dark .generation-progress-shell .gp-step-line {
          background: rgba(142, 218, 182, 0.12);
        }
        
        .dark .generation-progress-shell .gp-card {
          background: rgba(10, 38, 28, 0.75) !important;
          border-color: rgba(142, 218, 182, 0.12) !important;
        }
        
        .dark .generation-progress-shell .gp-panel {
          background: rgba(8, 32, 24, 0.65) !important;
          border-color: rgba(142, 218, 182, 0.12) !important;
        }
        
        .dark .generation-progress-shell .gp-eyebrow {
          color: #93AE9F;
        }
        
        .dark .generation-progress-shell .gp-title {
          color: #EAF7F0;
        }
        
        .dark .generation-progress-shell .gp-subtitle {
          color: #C7DDD0;
        }
        
        .dark .generation-progress-shell .gp-percent {
          color: #EAF7F0;
        }
        
        .dark .generation-progress-shell .gp-eta {
          color: #93AE9F;
        }
        
        .dark .generation-progress-shell .gp-progress-bar {
          background: rgba(142, 218, 182, 0.12);
        }
        
        .dark .generation-progress-shell .gp-status-line {
          color: #93AE9F;
        }
        
        .dark .generation-progress-shell .gp-mode-badge {
          background: rgba(16, 185, 129, 0.12);
          color: #7FD7A8;
        }
        
        .dark .generation-progress-shell .gp-stage-indicator,
        .dark .generation-progress-shell .gp-stage-wrapper {
          background: rgba(10, 38, 28, 0.65);
          border-color: rgba(142, 218, 182, 0.12);
        }
        
        .dark .generation-progress-shell .gp-stage-title {
          color: #EAF7F0;
        }
        
        .dark .generation-progress-shell .gp-stage-help {
          color: #93AE9F;
        }
        
        /* Letter Progress Box */
        .generation-progress-shell .gp-letter-progress-box {
          margin-top: 12px;
          padding: 12px 16px;
          background: linear-gradient(180deg, #f5fef8 0%, #ebf9f0 100%);
          border-radius: 10px;
          border: 1px solid rgba(18,163,107,0.2);
        }
        
        .dark .generation-progress-shell .gp-letter-progress-box {
          background: rgba(10, 38, 28, 0.65);
          border-color: rgba(142, 218, 182, 0.15);
        }
        
        /* DARK MODE: Stage list items */
        .dark .generation-progress-shell .gp-stage {
          background: rgba(10, 38, 28, 0.65) !important;
          border-color: rgba(142, 218, 182, 0.12) !important;
        }
        
        .dark .generation-progress-shell .gp-stage.active {
          background: linear-gradient(180deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.06) 100%) !important;
          border-color: rgba(16, 185, 129, 0.25) !important;
        }
        
        .dark .generation-progress-shell .gp-stage-icon {
          background: rgba(10, 38, 28, 0.75) !important;
          border-color: rgba(142, 218, 182, 0.15) !important;
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-stage.done .gp-stage-icon {
          background: var(--gp-green) !important;
          border-color: var(--gp-green) !important;
          color: #fff !important;
        }
        
        .dark .generation-progress-shell .gp-stage.active .gp-stage-icon {
          background: rgba(16, 185, 129, 0.18) !important;
          border-color: rgba(16, 185, 129, 0.30) !important;
          color: #34d399 !important;
        }
        
        .dark .generation-progress-shell .gp-stage-note {
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-stage-state {
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-stage.done .gp-stage-state,
        .dark .generation-progress-shell .gp-stage.active .gp-stage-state {
          color: #34d399 !important;
        }
        
        /* DARK MODE: Stat cards */
        .dark .generation-progress-shell .gp-stat {
          background: rgba(10, 38, 28, 0.65) !important;
          border-color: rgba(142, 218, 182, 0.12) !important;
        }
        
        .dark .generation-progress-shell .gp-stat-label {
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-stat-value {
          color: #EAF7F0 !important;
        }
        
        .dark .generation-progress-shell .gp-stat-note {
          color: #93AE9F !important;
        }
        
        /* DARK MODE: Notice box */
        .dark .generation-progress-shell .gp-notice {
          background: rgba(10, 38, 28, 0.65) !important;
          border-color: rgba(142, 218, 182, 0.12) !important;
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-notice.real-progress {
          background: rgba(16, 185, 129, 0.12) !important;
          border-color: rgba(16, 185, 129, 0.20) !important;
          color: #34d399 !important;
        }
        
        /* DARK MODE: Step dots */
        .dark .generation-progress-shell .gp-dot {
          background: rgba(10, 38, 28, 0.75) !important;
          border-color: rgba(142, 218, 182, 0.15) !important;
          color: #93AE9F !important;
        }
        
        .dark .generation-progress-shell .gp-dot.active {
          background: rgba(16, 185, 129, 0.12) !important;
          border-color: rgba(16, 185, 129, 0.30) !important;
          color: #34d399 !important;
        }
        
        /* DARK MODE: Recovery box */
        .dark .generation-progress-shell .gp-recovery-box {
          background: linear-gradient(180deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.08) 100%) !important;
          border-color: rgba(245, 158, 11, 0.30) !important;
        }
        
        /* DARK MODE: Footer note */
        .dark .generation-progress-shell .gp-footer-note {
          color: #93AE9F !important;
        }
        
        .generation-progress-shell .gp-letter-progress-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        
        .generation-progress-shell .gp-letter-progress-label {
          font-size: 13px;
          font-weight: 700;
          color: #0f4f2f;
          min-width: 0;
          flex: 1 1 auto;
          line-height: 1.4;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        
        .dark .generation-progress-shell .gp-letter-progress-label {
          color: #EAF7F0;
        }
        
        .generation-progress-shell .gp-letter-progress-badge {
          font-size: 11px;
          font-weight: 600;
          color: #12a36b;
          background: rgba(18,163,107,0.1);
          padding: 3px 8px;
          border-radius: 6px;
        }
        
        .dark .generation-progress-shell .gp-letter-progress-badge {
          background: rgba(16, 185, 129, 0.15);
          color: #7FD7A8;
        }
        
        .generation-progress-shell .gp-letter-progress-meta {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: #4a6b57;
        }
        
        .dark .generation-progress-shell .gp-letter-progress-meta {
          color: #93AE9F;
        }
        
        /* Recovery Box */
        .generation-progress-shell .gp-recovery-box {
          background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
          border: 1px solid rgba(245,158,11,0.3);
          border-radius: 12px;
          padding: 20px;
          margin-top: 12px;
        }
        
        .dark .generation-progress-shell .gp-recovery-box {
          background: rgba(245, 158, 11, 0.12);
          border-color: rgba(245, 158, 11, 0.20);
        }

        .generation-progress-shell .gp-top-step {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 18px;
          margin-bottom: 26px;
          color: var(--gp-muted);
          font-size: 14px;
          user-select: none;
        }

        .generation-progress-shell .gp-dot {
          width: 42px;
          height: 42px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          font-weight: 700;
          border: 1px solid var(--gp-line);
          background: #fff;
          color: var(--gp-muted);
        }

        .generation-progress-shell .gp-dot.done {
          background: var(--gp-green);
          color: #fff;
          border-color: var(--gp-green);
        }

        .generation-progress-shell .gp-dot.active {
          background: #fff;
          color: var(--gp-green-dark);
          border: 2px solid rgba(18,163,107,.35);
          box-shadow: 0 0 0 8px rgba(18,163,107,.08);
        }

        .generation-progress-shell .gp-step-line {
          width: 72px;
          height: 4px;
          border-radius: 999px;
          background: #dfe7e1;
          overflow: hidden;
          position: relative;
        }

        .generation-progress-shell .gp-step-line.done::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, var(--gp-green) 0%, #22c983 100%);
        }

        .generation-progress-shell .gp-card {
          background: rgba(255,255,255,.92);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(223,232,226,.9);
          border-radius: var(--gp-radius);
          box-shadow: var(--gp-shadow);
          overflow: hidden;
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }

        .generation-progress-shell .gp-card-inner {
          padding: 34px 34px 28px;
        }

        .generation-progress-shell .gp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 999px;
          background: var(--gp-green-soft);
          color: var(--gp-green-dark);
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .generation-progress-shell .gp-eyebrow.error {
          background: var(--gp-red-soft);
          color: var(--gp-red);
        }

        .generation-progress-shell .gp-title {
          font-size: 34px;
          line-height: 1.08;
          font-weight: 800;
          margin: 0 0 10px;
          letter-spacing: -.03em;
          color: var(--gp-text);
        }

        .generation-progress-shell .gp-subtitle {
          color: var(--gp-muted);
          font-size: 16px;
          margin: 0 0 26px;
        }

        .generation-progress-shell .gp-hero-grid {
          display: grid;
          grid-template-columns: 1.3fr .9fr;
          gap: 20px;
          width: 100%;
          min-width: 0;
        }

        .generation-progress-shell .gp-panel {
          border: 1px solid var(--gp-line);
          border-radius: 20px;
          background: #fff;
          padding: 22px;
          min-width: 0;
          max-width: 100%;
          overflow-x: hidden;
        }

        .generation-progress-shell .gp-progress-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }

        .generation-progress-shell .gp-percent {
          font-size: 36px;
          font-weight: 800;
          letter-spacing: -.04em;
          color: var(--gp-text);
        }

        .generation-progress-shell .gp-eta {
          font-size: 14px;
          color: var(--gp-muted);
          text-align: right;
          min-width: 0;
          flex: 1 1 auto;
        }

        .generation-progress-shell .gp-progress-bar {
          width: 100%;
          height: 14px;
          border-radius: 999px;
          background: #e8efea;
          overflow: hidden;
          position: relative;
        }

        .generation-progress-shell .gp-progress-fill {
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--gp-green) 0%, #2fd18b 100%);
          transition: width .35s ease;
          position: relative;
        }

        .generation-progress-shell .gp-progress-fill.error {
          background: linear-gradient(90deg, var(--gp-red) 0%, #f87171 100%);
        }

        .generation-progress-shell .gp-progress-fill::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 120px;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.25) 50%, transparent 100%);
          animation: gpShimmer 1.4s linear infinite;
        }

        @keyframes gpShimmer {
          from { transform: translateX(100%); }
          to { transform: translateX(-220%); }
        }

        .generation-progress-shell .gp-status-line {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-top: 14px;
          font-size: 15px;
          color: var(--gp-text);
          font-weight: 600;
          min-width: 0;
        }

        .generation-progress-shell .gp-status-line > span:last-child {
          min-width: 0;
          flex: 1 1 auto;
          line-height: 1.45;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .generation-progress-shell .gp-pulse {
          width: 11px;
          height: 11px;
          border-radius: 999px;
          background: var(--gp-green);
          box-shadow: 0 0 0 0 rgba(18,163,107,.45);
          animation: gpPulse 1.8s infinite;
          flex: 0 0 auto;
        }

        @keyframes gpPulse {
          0% { box-shadow: 0 0 0 0 rgba(18,163,107,.45); }
          70% { box-shadow: 0 0 0 10px rgba(18,163,107,0); }
          100% { box-shadow: 0 0 0 0 rgba(18,163,107,0); }
        }

        .generation-progress-shell .gp-support-copy {
          color: var(--gp-muted);
          font-size: 14px;
          line-height: 1.6;
          margin-top: 8px;
        }

        .generation-progress-shell .gp-stages {
          display: grid;
          gap: 10px;
          margin-top: 18px;
        }

        .generation-progress-shell .gp-stage {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 13px 14px;
          border-radius: 14px;
          border: 1px solid #edf2ee;
          background: #fbfdfc;
          transition: .2s ease;
        }

        .generation-progress-shell .gp-stage-left {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
          flex: 1 1 auto;
        }

        .generation-progress-shell .gp-stage-left > div {
          min-width: 0;
          flex: 1 1 auto;
        }

        .generation-progress-shell .gp-stage-icon {
          width: 28px;
          height: 28px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          font-size: 13px;
          font-weight: 800;
          border: 1px solid var(--gp-line);
          color: var(--gp-muted);
          background: #fff;
          flex: 0 0 auto;
        }

        .generation-progress-shell .gp-stage.done .gp-stage-icon {
          background: var(--gp-green);
          border-color: var(--gp-green);
          color: #fff;
        }

        .generation-progress-shell .gp-stage.active {
          border-color: rgba(18,163,107,.24);
          background: linear-gradient(180deg, #fbfffd 0%, #f2fbf7 100%);
        }

        .generation-progress-shell .gp-stage.active .gp-stage-icon {
          color: var(--gp-green-dark);
          border-color: rgba(18,163,107,.3);
          background: var(--gp-green-soft);
        }

        .generation-progress-shell .gp-stage-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--gp-text);
          line-height: 1.35;
          overflow-wrap: anywhere;
        }

        @media (min-width: 641px) {
          .generation-progress-shell .gp-stage-title {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .generation-progress-shell .gp-stage-note {
          font-size: 12px;
          color: var(--gp-muted);
          margin-top: 2px;
        }

        .generation-progress-shell .gp-stage-state {
          font-size: 12px;
          font-weight: 700;
          color: var(--gp-muted);
          flex: 0 0 auto;
        }

        .generation-progress-shell .gp-stage.done .gp-stage-state {
          color: var(--gp-green-dark);
        }

        .generation-progress-shell .gp-stage.active .gp-stage-state {
          color: var(--gp-green-dark);
        }

        .generation-progress-shell .gp-stats {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .generation-progress-shell .gp-stat {
          border: 1px solid var(--gp-line);
          border-radius: 18px;
          padding: 16px;
          background: #fff;
          min-height: 96px;
        }

        .generation-progress-shell .gp-stat-label {
          font-size: 12px;
          color: var(--gp-muted);
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 8px;
          font-weight: 700;
        }

        .generation-progress-shell .gp-stat-value {
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -.03em;
          margin-bottom: 4px;
          color: var(--gp-text);
        }

        .generation-progress-shell .gp-stat-note {
          color: var(--gp-muted);
          font-size: 13px;
          line-height: 1.45;
        }

        .generation-progress-shell .gp-notice {
          border: 1px solid rgba(212,167,53,.25);
          background: var(--gp-gold-soft);
          color: #7b5a08;
          border-radius: 18px;
          padding: 16px;
          font-size: 14px;
          line-height: 1.55;
        }

        .generation-progress-shell .gp-notice.real-progress {
          border-color: rgba(18,163,107,.25);
          background: var(--gp-green-soft);
          color: var(--gp-green-dark);
        }

        .generation-progress-shell .gp-success {
          display: none;
          margin-top: 18px;
          padding: 16px 18px;
          border-radius: 18px;
          background: var(--gp-green-soft);
          border: 1px solid rgba(18,163,107,.18);
          color: var(--gp-green-dark);
          font-weight: 700;
        }

        .generation-progress-shell .gp-success.show {
          display: block;
          animation: gpFadeIn .35s ease;
        }

        .generation-progress-shell .gp-error {
          display: none;
          margin-top: 18px;
          padding: 16px 18px;
          border-radius: 18px;
          background: var(--gp-red-soft);
          border: 1px solid rgba(220,38,38,.18);
          color: var(--gp-red);
          font-weight: 700;
        }

        .generation-progress-shell .gp-error.show {
          display: flex;
          align-items: center;
          gap: 12px;
          animation: gpFadeIn .35s ease;
        }

        @keyframes gpFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .generation-progress-shell .gp-footer-note {
          text-align: center;
          color: var(--gp-muted);
          font-size: 13px;
          margin-top: 16px;
        }

        .generation-progress-shell .gp-mode-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .generation-progress-shell .gp-mode-badge.real {
          background: var(--gp-green-soft);
          color: var(--gp-green-dark);
        }

        .generation-progress-shell .gp-mode-badge.simulated {
          background: var(--gp-gold-soft);
          color: #7b5a08;
        }

        @media (max-width: 900px) {
          .generation-progress-shell .gp-hero-grid {
            grid-template-columns: 1fr;
          }
          .generation-progress-shell .gp-title {
            font-size: 28px;
          }
          .generation-progress-shell .gp-percent {
            font-size: 30px !important;
          }
          .generation-progress-shell .gp-top-step {
            gap: 12px;
            flex-wrap: wrap;
          }
          .generation-progress-shell .gp-step-line {
            width: 46px;
          }
        }

        @media (max-width: 640px) {
          .generation-progress-shell {
            --gp-radius: 16px;
          }
          .generation-progress-shell .gp-card-inner {
            padding: 16px 14px 14px;
          }
          .generation-progress-shell .gp-title {
            font-size: 22px;
            word-break: break-word;
          }
          .generation-progress-shell .gp-subtitle {
            font-size: 14px;
            word-break: break-word;
            margin-bottom: 18px;
          }
          .generation-progress-shell .gp-eyebrow {
            flex-wrap: wrap;
            font-size: 12px;
          }
          .generation-progress-shell .gp-panel {
            padding: 14px;
            border-radius: 14px;
          }
          .generation-progress-shell .gp-progress-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }
          .generation-progress-shell .gp-eta {
            text-align: left;
            width: 100%;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .generation-progress-shell .gp-status-line {
            flex-wrap: wrap;
            font-size: 14px;
          }
          .generation-progress-shell .gp-stats {
            grid-template-columns: 1fr;
          }
          .generation-progress-shell .gp-stat {
            min-height: auto;
            min-width: 0;
            padding: 14px;
          }
          .generation-progress-shell .gp-stat-value {
            font-size: 22px;
            word-break: break-word;
          }
          .generation-progress-shell .gp-stat-note {
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .generation-progress-shell .gp-stage {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }
          .generation-progress-shell .gp-stage-left {
            width: 100%;
          }
          .generation-progress-shell .gp-stage-title {
            white-space: normal;
            overflow: visible;
            text-overflow: unset;
          }
          .generation-progress-shell .gp-stage-note {
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .generation-progress-shell .gp-stage-state {
            align-self: flex-start;
            padding-left: 40px;
          }
          .generation-progress-shell .gp-letter-progress-box {
            padding: 10px 12px;
          }
          .generation-progress-shell .gp-letter-progress-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .generation-progress-shell .gp-letter-progress-meta {
            flex-direction: column;
            gap: 4px;
            word-break: break-word;
          }
          .generation-progress-shell .gp-notice {
            word-break: break-word;
            overflow-wrap: anywhere;
            padding: 12px;
          }
          .generation-progress-shell .gp-footer-note {
            font-size: 12px;
            line-height: 1.5;
          }
        }
      `}</style>

      {/* Wizard Steps Indicator - ONLY render if not hidden (parent may already have one) */}
      {!hideWizardStepper && (
        <div className="gp-top-step" aria-label="Wizard steps">
          {/* This section intentionally empty when hideWizardStepper is true */}
        </div>
      )}

      <div className="gp-card">
        <div className="gp-card-inner">
          {preflightFailure ? (
            <div data-testid="preflight-validation-failure">
              <div className="gp-eyebrow error">Could not start</div>
              <h1 className="gp-title">{preflightFailure.title}</h1>
              <p className="gp-subtitle">{preflightFailure.body}</p>
              {preflightFailure.detail && process.env.NODE_ENV !== 'production' && (
                <div className="gp-error show" style={{ marginTop: 12 }}>
                  <AlertCircle size={20} />
                  <span>{preflightFailure.detail}</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  data-testid="retry-generation-btn"
                  onClick={() => {
                    setPreflightFailure(null);
                    setError(null);
                    hasStartedRef.current = false;
                    if (onRetryGeneration) {
                      onRetryGeneration();
                    } else {
                      startRealGeneration();
                    }
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Retry Generation
                </button>
                {onBackToReview && (
                  <button
                    type="button"
                    data-testid="back-to-review-btn"
                    onClick={onBackToReview}
                    style={{
                      padding: '12px 24px',
                      background: 'white',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Back to Review
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
          <div className={`gp-eyebrow ${error ? 'error' : ''}`}>
            {error ? 'Generation Failed' : isComplete ? '✓ Complete' : 'Generating letters'}
            {process.env.NODE_ENV !== 'production' && useSimulation && (
              <span className="gp-mode-badge simulated">Demo</span>
            )}
            {!(process.env.NODE_ENV !== 'production' && useSimulation) && (
              <span className="gp-mode-badge real">Live</span>
            )}
          </div>
          <h1 className="gp-title">Building your dispute letters</h1>
          <p className="gp-subtitle">
            {error 
              ? 'An error occurred during generation. Please try again.'
              : `Generating ${actualTotalLetters || totalLetters} letter${(actualTotalLetters || totalLetters) !== 1 ? 's' : ''} across ${totalPackages} package${totalPackages !== 1 ? 's' : ''}`
            }
          </p>

          <div className="gp-hero-grid">
            {/* Left Panel: Progress */}
            <div className="gp-panel">
              <div className="gp-progress-wrap">
                <div className="gp-progress-meta">
                  <div className="gp-percent">{!isNaN(progressPercent) && progressPercent >= 0 ? `${progressPercent}%` : 'Preparing...'}</div>
                  <div className="gp-eta">
                    <div>{statusDisplay}</div>
                  </div>
                </div>

                <div className="gp-progress-bar" aria-label="Letter generation progress">
                  <div 
                    className={`gp-progress-fill ${error ? 'error' : ''}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="gp-status-line">
                  {!isComplete && !error && <span className="gp-pulse" />}
                  <span>
                    {error ? 'Generation failed' : isComplete ? 'Generation finished' : message}
                  </span>
                </div>
                
                {/* Letter-by-letter progress indicator - THE REAL PROGRESS */}
                {letterProgress && (
                  <div className="gp-letter-progress-box">
                    <div className="gp-letter-progress-header">
                      <span className="gp-letter-progress-label">
                        <FileText size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
                        Overall: Letter {letterProgress.current_letter} of {actualTotalLetters}
                      </span>
                      <span className="gp-letter-progress-badge">
                        Processing
                      </span>
                    </div>
                    <div className="gp-letter-progress-meta">
                      {letterProgress.package_label && (
                        <span><strong>Package:</strong> {letterProgress.package_label}</span>
                      )}
                      {letterProgress.bureau && (
                        <span><strong>Bureau:</strong> {letterProgress.bureau}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Error/Failure Recovery Panel */}
                {recoveryMode ? (
                  <div className="gp-recovery-box">
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px',
                    }}>
                      <AlertTriangle size={24} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>
                          Connection Lost - Generation May Still Be Running
                        </div>
                        <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                          {message}
                        </div>
                        
                        {/* Recovery status display */}
                        {recoveryStatus && (
                          <div style={{
                            background: 'rgba(255,255,255,0.7)',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            fontSize: '13px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span style={{ color: '#78350f' }}>Progress:</span>
                              <span style={{ fontWeight: 600, color: '#92400e' }}>
                                {recoveryStatus.completed} of {recoveryStatus.total} letters
                              </span>
                            </div>
                            {recoveryStatus.failed > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ color: '#991b1b' }}>Failed:</span>
                                <span style={{ fontWeight: 600, color: '#991b1b' }}>
                                  {recoveryStatus.failed} letters
                                </span>
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#78350f' }}>Worker Status:</span>
                              <span style={{ 
                                fontWeight: 600, 
                                color: recoveryStatus.workerActive ? '#059669' : '#6b7280'
                              }}>
                                {recoveryStatus.workerActive ? '● Running' : '○ Stopped'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Single recovery action button - only when a live job existed and stream disconnected */}
                    {sessionId && (jobId || sessionStorage.getItem(`generation_run_${sessionId}`)) && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={handleReconnect}
                        disabled={isReconnecting || isRefreshingStatus}
                        data-testid="reconnect-live-updates-btn"
                        style={{
                          padding: '12px 24px',
                          background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          cursor: (isReconnecting || isRefreshingStatus) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: (isReconnecting || isRefreshingStatus) ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {(isReconnecting || isRefreshingStatus) ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RefreshCw size={16} />
                        )}
                        Reconnect Live Updates
                      </button>
                      
                      {/* Retry failed letters button - only shown when there are actual failures */}
                      {recoveryStatus?.failed > 0 && (
                        <button
                          onClick={handleRetryFailed}
                          disabled={isRetrying}
                          data-testid="retry-failed-btn"
                          style={{
                            padding: '12px 24px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: isRetrying ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            opacity: isRetrying ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          {isRetrying && <Loader2 size={16} className="animate-spin" />}
                          Retry {recoveryStatus.failed} Failed Letters
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                ) : error ? (
                  <div style={{
                    background: 'rgba(220, 38, 38, 0.05)',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginTop: '12px',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px',
                    }}>
                      <AlertTriangle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
                          Generation Error
                        </div>
                        <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
                          {error}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          setError(null);
                          setIsComplete(false);
                          if (sessionId && fetchGenerationResultRef.current) {
                            fetchGenerationResultRef.current(sessionId, true);
                          }
                        }}
                        disabled={!sessionId}
                        data-testid="retry-final-package-btn"
                        style={{
                          padding: '10px 20px',
                          background: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          cursor: !sessionId ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          opacity: !sessionId ? 0.7 : 1,
                        }}
                      >
                        Retry
                      </button>
                      <button
                        onClick={handleReconnect}
                        disabled={isReconnecting || isRefreshingStatus}
                        data-testid="reconnect-error-btn"
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          cursor: (isReconnecting || isRefreshingStatus) ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          opacity: (isReconnecting || isRefreshingStatus) ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {(isReconnecting || isRefreshingStatus) ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <RefreshCw size={14} />
                        )}
                        Reconnect Live Updates
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="gp-support-copy">
                    {isComplete 
                      ? 'Continuing to Add Supporting Documents automatically.' 
                      : currentStageData.help
                    }
                  </div>
                )}
              </div>

              {/* Stages List - REAL stages only */}
              <div className="gp-stages">
                {REAL_STAGES.map((stage, index) => {
                  const isDone = completedStages.includes(stage.key) || (isComplete && !error);
                  const isActive = displayStage === stage.key && !isComplete && !error;
                  
                  return (
                    <div 
                      key={stage.key}
                      className={`gp-stage ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                      data-testid={`stage-${stage.key}`}
                    >
                      <div className="gp-stage-left">
                        <div className="gp-stage-icon">
                          {isDone ? <Check size={14} /> : index + 1}
                        </div>
                        <div>
                          <div className="gp-stage-title">{stage.label}</div>
                          <div className="gp-stage-note">{stage.help}</div>
                        </div>
                      </div>
                      <div className="gp-stage-state">
                        {isDone ? 'Done' : isActive ? 'In progress' : 'Pending'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Success Message */}
              <div className={`gp-success ${isComplete && !error ? 'show' : ''}`}>
                Letters generated successfully. Continuing to Add Supporting Documents…
              </div>

              {/* Error Message */}
              <div className={`gp-error ${error ? 'show' : ''}`}>
                <AlertCircle size={20} />
                <span>{error || 'An unexpected error occurred'}</span>
              </div>
            </div>

            {/* Right Panel: Stats - Uses Step 3's resolved totals */}
            <div className="gp-panel">
              <div className="gp-stats">
                <div className="gp-stat">
                  <div className="gp-stat-label">Total Items</div>
                  <div className="gp-stat-value">{totalItems || (selectedAccountCount + selectedInquiryCount + selectedPiCount)}</div>
                  <div className="gp-stat-note">Items proceeding in this dispute run</div>
                </div>

                <div className="gp-stat">
                  <div className="gp-stat-label">Packages</div>
                  <div className="gp-stat-value">{totalPackages || '—'}</div>
                  <div className="gp-stat-note">Grouped by method and item type</div>
                </div>

                <div className="gp-stat">
                  <div className="gp-stat-label">Total Letters</div>
                  <div className="gp-stat-value">{actualTotalLetters || totalLetters || '—'}</div>
                  <div className="gp-stat-note">One per package per bureau</div>
                </div>

                <div className="gp-stat">
                  <div className="gp-stat-label">Accounts / Inquiries / PI</div>
                  <div className="gp-stat-value">
                    {selectedAccountCount} / {selectedInquiryCount} / {selectedPiCount}
                  </div>
                  <div className="gp-stat-note">Breakdown by item type</div>
                </div>
              </div>

              <div className={`gp-notice ${!useSimulation ? 'real-progress' : ''}`}>
                {useSimulation && process.env.NODE_ENV !== 'production'
                  ? 'Demo mode: Using simulated progress.'
                  : 'Live mode: Progress reflects actual backend generation stages.'
                }
              </div>

              {roundId && (
                <div style={{ marginTop: '12px', fontSize: '12px', color: '#66736d', wordBreak: 'break-all' }}>
                  Round ID: <span style={{ fontFamily: 'monospace' }}>{roundId.slice(0, 16)}...</span>
                </div>
              )}

              {sessionId && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#66736d', wordBreak: 'break-all' }}>
                  Session: <span style={{ fontFamily: 'monospace' }}>{sessionId.slice(0, 8)}...</span>
                </div>
              )}
            </div>
          </div>

          <div className="gp-footer-note">
            {useSimulation && process.env.NODE_ENV !== 'production'
              ? 'This estimate uses simulated timing for development.'
              : 'Progress updates from the backend generation pipeline.'
            }
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LetterGenerationProgress;
